import React, { useState, useEffect } from "react";
import { Button, Table, Badge, Card, CardBody, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import Loading from "../../components/Loading";
import { fetchProductsByBrand, deleteProduct } from "../../utils/apiService";

const ProductListComponent = () => {
  const { brandId } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, productId: null, productName: '' });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently().catch(() => null);
        const data = await fetchProductsByBrand(brandId, token);
        const list = Array.isArray(data?.productList) ? data.productList : (Array.isArray(data) ? data : []);
        setProducts(list.map((p) => ({ id: p.id || p.Id || 0, name: p.name || '' })));
      } catch (err) {
        setError(err?.message || "Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [brandId, getAccessTokenSilently]);

  const openDeleteModal = (productId, productName, e) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, productId, productName });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      await deleteProduct(deleteModal.productId, token);

      // Remove from products list
      setProducts(products.filter(p => p.id !== deleteModal.productId));
      setDeleteModal({ isOpen: false, productId: null, productName: '' });
    } catch (err) {
      setError(err?.message || "Failed to delete product");
      setDeleteModal({ isOpen: false, productId: null, productName: '' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button color="link" className="p-0 me-2" onClick={() => history.push('/brands')}>
            Brands
          </Button>
          <span className="text-muted"> &gt; Products</span>
        </div>
        <Button color="primary" onClick={() => history.push(`/brands/${brandId}/products/create`)}>
          Add Product
        </Button>
      </div>

      {error && <Alert color="danger">{error}</Alert>}

      {loading ? (
        <Card>
          <CardBody className="text-center">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 mb-0">Loading products...</p>
          </CardBody>
        </Card>
      ) : (
        <>
          <Table striped responsive hover>
            <thead className="table-dark">
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Configs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length > 0 ? (
                products.map((p) => (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => history.push(`/brands/${brandId}/products/${p.id}/configs`)}>
                    <td><strong style={{ color: '#0d6efd' }}>{p.name}</strong></td>
                    <td><Badge color="secondary">{p.id}</Badge></td>
                    <td><span className="text-muted small">View Configs →</span></td>
                    <td>
                      <Button
                        color="outline-danger"
                        size="sm"
                        onClick={(e) => openDeleteModal(p.id, p.name, e)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <h6 className="text-muted mb-2">No Products Yet</h6>
                    <p className="text-muted mb-0">Add your first product to get started.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <Modal isOpen={deleteModal.isOpen} toggle={() => setDeleteModal({ isOpen: false, productId: null, productName: '' })} centered>
            <ModalHeader toggle={() => setDeleteModal({ isOpen: false, productId: null, productName: '' })}>
              Delete Product
            </ModalHeader>
            <ModalBody>
              Are you sure you want to delete '{deleteModal.productName}'? This action cannot be undone.
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setDeleteModal({ isOpen: false, productId: null, productName: '' })}>
                Cancel
              </Button>
              <Button color="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </div>
  );
};

export default withAuthenticationRequired(ProductListComponent, {
  onRedirecting: () => <Loading />,
});
